import React from 'react';
export const CmsPageContext = React.createContext({});

/*
 * The back-link slot in the header of the page on screen, for a back link that is rendered
 * beside its CmsPage rather than inside it.
 *
 * Most editors are written `<><BreadcrumbLink /><CmsPage>...</CmsPage></>`, so the link is a
 * sibling of the page and cannot read CmsPageContext. CmsPage publishes its slot here as
 * well as through the context, and a back link outside any CmsPage follows whichever slot
 * was published last and is still mounted. A page with no CmsPage publishes nothing, and its
 * back link stays where it is written.
 *
 * A stack rather than a single value, so that one page mounting in the same commit as the
 * previous one unmounts cannot leave the slot pointing at the page that has gone.
 */
const slots = [];
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export const pageBackSlot = {
  current: () => slots.at(-1) ?? null,
  /** Returns the function that takes this node back out. */
  publish: (node) => {
    slots.push(node);
    notify();
    return () => {
      const at = slots.lastIndexOf(node);
      if (at > -1) slots.splice(at, 1);
      notify();
    };
  },
  subscribe: (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/*
 * The title of the page on screen, as text, for the breadcrumbs in the frame's top bar
 * (`topbar/components/AppTopBar.js`). The bar is outside every CmsPage, so the context does
 * not reach it; CmsPage publishes its title here instead. A stack for the same reason as the
 * back-link slot: one page mounting in the same commit as the previous one unmounts must not
 * leave the old title behind.
 */
const titles = [];
const titleListeners = new Set();
const notifyTitle = () => titleListeners.forEach((listener) => listener());

export const pageTitle = {
  current: () => titles.at(-1)?.text ?? '',
  /** Returns the function that takes this title back out. */
  publish: (text) => {
    const entry = { text };
    titles.push(entry);
    notifyTitle();
    return () => {
      const at = titles.lastIndexOf(entry);
      if (at > -1) titles.splice(at, 1);
      notifyTitle();
    };
  },
  subscribe: (listener) => {
    titleListeners.add(listener);
    return () => titleListeners.delete(listener);
  },
};

/*
 * The page's way back, for the breadcrumbs: `{ label, onClick, target }` from the back link
 * the page would have drawn above its title ("Back to Collections"). The breadcrumbs show
 * it instead, so a page has one way back rather than two (BackButton.js, AppTopBar).
 *
 * `onClick` is the page's own handler, so a crumb goes back exactly as the link did: to the
 * list the admin came from, where they left it (SURF-4843). `target` is the plugin page it
 * goes to, `{ pluginName, pageName, params }`, when the link knows it, which is how the
 * breadcrumbs tell a link back to the rail's own screen from one to a record above this one.
 *
 * The first entry is the one shown, as the header showed the first back link in the page's
 * order when a page drew two (simple-sidebar.css).
 */
const backs = [];
const backListeners = new Set();
const notifyBack = () => backListeners.forEach((listener) => listener());

export const pageBack = {
  current: () => backs[0] ?? null,
  /** Returns the function that takes this entry back out. */
  publish: (entry) => {
    backs.push(entry);
    notifyBack();
    return () => {
      const at = backs.indexOf(entry);
      if (at > -1) backs.splice(at, 1);
      notifyBack();
    };
  },
  subscribe: (listener) => {
    backListeners.add(listener);
    return () => backListeners.delete(listener);
  },
};

/** What a back link says it goes back to: "Back to Collections" is Collections. */
export const backLabel = (text) =>
  String(text ?? '')
    .replace(/^back( to)?\b/i, '')
    .trim();

/**
 * A title as plain text. CmsPage takes a node and a few pages pass one (`<em>Images</em>`,
 * a label in a fragment), so the text is read out of it; a node with no text gives ''.
 */
export const titleText = (node) => {
  const walk = (value) => {
    if (value == null || typeof value === 'boolean') return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map(walk).join('');
    return walk(value.props?.children);
  };
  return walk(node).replaceAll(/\s+/g, ' ').trim();
};
